<template>
  <div class="retencao">
    <b-card>
      <b-row align-v="center">
        <b-col>
          <h2 class="mb-0">Retenção de Distribuidores</h2>
          <small v-if="competencia" class="text-muted">
            Ativos em {{ formataData(competencia.data_referencia) }} · atualizado pelo Protheus em
            {{ formataDataHora(competencia.apurado_em) }}
          </small>
        </b-col>
        <b-col cols="12" md="auto" class="mt-2 mt-md-0">
          <b-button v-if="podeCadastrarMeta" variant="outline-primary" size="sm" class="mr-3" href="/adm/retencao/metas">
            <i class="fas fa-bullseye"></i> Metas das analistas
          </b-button>
          <label class="mb-0 mr-2">Competência</label>
          <b-form-select
            v-model="competenciaSel"
            :options="competencias.map(c => ({ value: c, text: nomeCompetencia(c) }))"
            size="sm"
            style="width: 170px"
            :disabled="loading"
            @change="carregar()"
          ></b-form-select>
        </b-col>
      </b-row>

      <div v-if="loading && !competencia" class="text-center my-5">
        <b-spinner></b-spinner>
      </div>

      <div v-else-if="!competencia" class="text-center text-muted my-5">
        Nenhuma competência recebida do Protheus ainda (job U_JOBRETEN).
      </div>

      <template v-else>
        <!-- Resumo: sempre sobre a regiao da analista -->
        <div class="mt-3 mb-1 text-muted small">
          Meta {{ regiaoMeta ? 'da Região ' + regiaoMeta : 'de todas as regiões' }}
        </div>
        <b-row class="resumo">
          <b-col cols="6" md="3">
            <div class="card-resumo">
              <div class="titulo">Ativos</div>
              <div class="valor">{{ resumo.ativos }}</div>
              <div class="det">Master {{ resumo.ativosA }} · Gestora {{ resumo.ativosG }}</div>
              <div v-if="foraBase.novos || foraBase.reativados" class="det">
                + fora da meta em {{ rotuloMesSo(mesCompetencia) }}:
                <a v-if="foraBase.novos" href="#" @click.prevent="filtrarMes('cadastro')">{{ foraBase.novos }} novo(s)</a>
                <template v-if="foraBase.novos && foraBase.reativados"> · </template>
                <a v-if="foraBase.reativados" href="#" @click.prevent="filtrarMes('reativacao')">
                  {{ foraBase.reativados }} reativado(s)
                </a>
              </div>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="card-resumo verde">
              <div class="titulo">Retidos</div>
              <div class="valor">{{ resumo.retidos }}</div>
              <div class="det">Master {{ resumo.retidosA }} · Gestora {{ resumo.retidosG }}</div>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="card-resumo vermelho">
              <div class="titulo">Não retidos</div>
              <div class="valor">{{ resumo.ativos - resumo.retidos }}</div>
              <div class="det">{{ resumo.semMotivo }} sem motivo registrado</div>
            </div>
          </b-col>
          <b-col cols="6" md="3">
            <div class="card-resumo">
              <div class="titulo">% Retenção</div>
              <div class="valor">{{ formataPct(resumo.pct) }}</div>
              <b-progress :max="100" height="6px" class="mt-1">
                <b-progress-bar
                  :value="resumo.pct"
                  :variant="faixas.length && resumo.pct >= faixas[0].meta ? 'success' : 'danger'"
                ></b-progress-bar>
              </b-progress>
            </div>
          </b-col>
          <b-col cols="12" lg="6">
            <div class="card-resumo">
              <div class="titulo">
                Metas {{ nomeCompetencia(competencia.competencia) }}
                <span v-if="metaAnalista" class="text-lowercase">· {{ metaAnalista.nome }}</span>
              </div>
              <div v-if="faixas.length" class="faixas-wrap">
                <table class="faixas">
                  <tr>
                    <th></th>
                    <th v-for="f in faixas" :key="'p' + f.faixa" :class="{ batida: f.faltam <= 0 }">
                      {{ f.premio !== null ? formataReal(f.premio) : f.faixa + 'ª faixa' }}
                      <i v-if="f.faltam <= 0" class="fas fa-check"></i>
                    </th>
                  </tr>
                  <tr>
                    <td>Precisa reter</td>
                    <td v-for="f in faixas" :key="'n' + f.faixa" class="ok">{{ f.precisa }}</td>
                  </tr>
                  <tr>
                    <td>Faltam</td>
                    <td v-for="f in faixas" :key="'f' + f.faixa" :class="f.faltam <= 0 ? 'ok' : 'falta'">
                      {{ f.faltam <= 0 ? 'batida' : f.faltam }}
                    </td>
                  </tr>
                  <tr>
                    <td>% atingido</td>
                    <td v-for="f in faixas" :key="'a' + f.faixa" class="atingido">{{ formataPct(f.atingido) }}</td>
                  </tr>
                  <tr>
                    <td>Meta</td>
                    <td v-for="f in faixas" :key="'m' + f.faixa" class="ok">{{ formataPct(f.meta) }}</td>
                  </tr>
                </table>
                <div v-if="metasDaRegiao.length > 1" class="det mt-1">
                  Outras analistas da região: {{ metasDaRegiao.filter(m => m !== metaAnalista).map(m => m.nome).join(', ') }}
                </div>
              </div>
              <div v-else class="det mt-2">
                {{ regiaoMeta ? 'Meta não cadastrada para este mês.' : 'Escolha uma região para ver a meta.' }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" lg="6">
            <div class="card-resumo">
              <div class="titulo d-flex justify-content-between">
                <span>
                  Vendas digitadas · {{ filtro.regiao ? 'Região ' + filtro.regiao : 'todas as regiões' }}
                </span>
                <span class="troca-metrica">
                  <a href="#" :class="{ ativo: metricaGrafico === 'valor_mes' }" @click.prevent="metricaGrafico = 'valor_mes'">R$</a>
                  <a href="#" :class="{ ativo: metricaGrafico === 'qtd_mes' }" @click.prevent="metricaGrafico = 'qtd_mes'">itens</a>
                </span>
              </div>
              <div class="totais">
                <div>
                  <div class="rot">{{ rotuloMes(mesCompetencia) }}</div>
                  <div class="num">{{ formataQtd(totais.mes.qtd) || '0' }} <small>itens</small></div>
                  <div class="num-rs">{{ formataReal(totais.mes.valor) }}</div>
                  <div :class="['var', classeVar(totais.mes.varValor)]" :title="'Mesmo mês de ' + anoAnterior + ': ' + formataReal(totais.mes.valorAnt) + ' · ' + formataQtd(totais.mes.qtdAnt) + ' itens'">
                    {{ formataVar(totais.mes.varValor) }} R$ vs {{ rotuloMes(mesAnoAnterior) }}
                  </div>
                  <div :class="['var', classeVar(totais.mes.varQtd)]">
                    {{ formataVar(totais.mes.varQtd) }} itens ({{ formataDif(totais.mes.qtd - totais.mes.qtdAnt) }})
                  </div>
                </div>
                <div>
                  <div class="rot">Acumulado jan–{{ rotuloMesSo(mesCompetencia) }}/{{ String(anoAtual).substr(2) }}</div>
                  <div class="num">{{ formataQtd(totais.acum.qtd) || '0' }} <small>itens</small></div>
                  <div class="num-rs">{{ formataReal(totais.acum.valor) }}</div>
                  <div :class="['var', classeVar(totais.acum.varValor)]" :title="'Mesmo período de ' + anoAnterior + ': ' + formataReal(totais.acum.valorAnt) + ' · ' + formataQtd(totais.acum.qtdAnt) + ' itens'">
                    {{ formataVar(totais.acum.varValor) }} R$ vs jan–{{ rotuloMesSo(mesCompetencia) }}/{{ String(anoAnterior).substr(2) }}
                  </div>
                  <div :class="['var', classeVar(totais.acum.varQtd)]">
                    {{ formataVar(totais.acum.varQtd) }} itens ({{ formataDif(totais.acum.qtd - totais.acum.qtdAnt) }})
                  </div>
                </div>
              </div>
              <svg class="grafico" :viewBox="'0 0 ' + grafico.largura + ' ' + grafico.altura">
                <line :x1="0" :x2="grafico.largura" :y1="grafico.base" :y2="grafico.base" class="eixo" />
                <g v-for="b in grafico.barras" :key="b.mes">
                  <rect :x="b.x" :y="b.yAnt" :width="b.w" :height="grafico.base - b.yAnt" class="b-ant">
                    <title>{{ b.rotuloAnt }}</title>
                  </rect>
                  <rect :x="b.x + b.w + 2" :y="b.yAtu" :width="b.w" :height="grafico.base - b.yAtu" :class="['b-atu', { atual: b.atual }]">
                    <title>{{ b.rotuloAtu }}</title>
                  </rect>
                  <text :x="b.x + b.w + 1" :y="grafico.altura - 3" class="rot-mes">{{ b.nome }}</text>
                  <text
                    v-if="b.difQtd !== null"
                    :x="b.x + b.w + 1"
                    :y="Math.min(b.yAnt, b.yAtu) - 4"
                    :class="['dif', b.difQtd >= 0 ? 'sobe' : 'desce']"
                  >
                    {{ b.difCurta }}
                    <title>{{ b.rotuloDif }}</title>
                  </text>
                </g>
              </svg>
              <div class="legenda-graf">
                <span><i class="b-ant"></i> {{ anoAnterior }}</span>
                <span><i class="b-atu"></i> {{ anoAtual }}</span>
                <span class="text-muted">· mesmos distribuidores da base de ativos</span>
              </div>
            </div>
          </b-col>
        </b-row>

        <div v-if="resumo.motivos.length" class="motivos mt-2">
          <span class="text-muted small mr-2">Motivos:</span>
          <b-badge
            v-for="m in resumo.motivos"
            :key="m.motivo"
            :variant="filtro.motivo === m.motivo ? 'primary' : 'light'"
            class="mr-1 mb-1 motivo-badge"
            @click="filtro.motivo = filtro.motivo === m.motivo ? null : m.motivo"
          >
            {{ m.motivo }} <b>{{ m.qtd }}</b> ({{ formataPct(m.qtd / resumo.ativos * 100) }})
          </b-badge>
        </div>

        <!-- Filtros -->
        <b-row align-v="end" class="mt-3">
          <b-col cols="6" md="2">
            <label>Região</label>
            <b-form-select v-model="filtro.regiao" :options="opcoesRegiao" size="sm"></b-form-select>
          </b-col>
          <b-col cols="6" md="1">
            <label>Classe</label>
            <b-form-select v-model="filtro.classe" :options="opcoesClasse" size="sm"></b-form-select>
          </b-col>
          <b-col cols="6" md="2">
            <label>Retenção</label>
            <b-form-select v-model="filtro.retido" :options="opcoesRetido" size="sm"></b-form-select>
          </b-col>
          <b-col cols="6" md="1">
            <label>Semana</label>
            <b-form-select v-model="filtro.semana" :options="opcoesSemana" size="sm"></b-form-select>
          </b-col>
          <b-col cols="6" md="2">
            <label>Cadastro</label>
            <b-form-select v-model="filtro.cadastro" :options="opcoesPeriodo('cadastro')" size="sm"></b-form-select>
          </b-col>
          <b-col cols="6" md="2">
            <label>Reativação</label>
            <b-form-select v-model="filtro.reativacao" :options="opcoesPeriodo('reativacao')" size="sm"></b-form-select>
          </b-col>
          <b-col cols="12" md="2">
            <label>Busca</label>
            <b-form-input
              v-model="filtro.busca"
              size="sm"
              placeholder="Código, nome, cidade, consultor ou motivo"
              debounce="300"
            ></b-form-input>
          </b-col>
          <b-col cols="12" class="text-md-right mt-2">
            <b-form-checkbox v-model="mostraAnoAnterior" switch size="sm" class="d-inline-block mr-3">
              Meses de {{ anoAnterior }}
            </b-form-checkbox>
            <b-button variant="success" size="sm" :disabled="loading" @click="exportar()">
              <i class="far fa-file-excel"></i> Exportar Excel
            </b-button>
          </b-col>
        </b-row>

        <div class="d-flex flex-wrap justify-content-between align-items-center mt-3 mb-2">
          <div class="small text-muted">
            <b>{{ filtrados.length }}</b> distribuidor(es)
            <span v-if="filtro.motivo">
              · motivo "{{ filtro.motivo }}"
              <a href="#" @click.prevent="filtro.motivo = null">limpar</a>
            </span>
          </div>
          <div class="legenda">
            <span><i class="cel n0"></i> sem pedido</span>
            <span><i class="cel n1"></i> abaixo da média</span>
            <span><i class="cel n2"></i> na média</span>
            <span><i class="cel n3"></i> acima da média</span>
          </div>
        </div>

        <!-- Grade -->
        <div class="grade">
          <table class="tabela">
            <thead>
              <tr>
                <th class="col-dist">Distribuidor</th>
                <th class="col-hist">
                  <div :class="['hist', { 'com-ano': mostraAnoAnterior }]">
                    <span v-if="mostraAnoAnterior" class="ano-cab"></span>
                    <span
                      v-for="m in linhasHist[linhasHist.length - 1].meses"
                      :key="'h' + m"
                      :class="['mes-cab', { atual: !mostraAnoAnterior && m === mesCompetencia }]"
                    >{{ mostraAnoAnterior ? rotuloMesSo(m) : rotuloMesCurto(m) }}</span>
                  </div>
                </th>
                <th class="col-num">Média<br />{{ anoAnterior }}</th>
                <th class="col-num">Média<br />{{ anoAtual }}</th>
                <th class="col-mes">{{ rotuloMes(mesCompetencia) }}</th>
                <th class="col-semana" title="Semana do mês em que o distribuidor costuma comprar (R$ dos últimos 12 meses fechados)">
                  Semana<br />de compra
                </th>
                <th class="col-contato">Últ. contato</th>
                <th class="col-motivo">Motivo s/ pedido</th>
                <th class="col-obs">Observação</th>
                <th class="col-cat" title="Catálogos do último envio (Z46 com pedido gerado)">Catálogos</th>
                <th class="col-opcat">Op. Cat</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filtrados.length">
                <td colspan="11" class="vazio">Nenhum distribuidor encontrado</td>
              </tr>
              <tr
                v-for="item in pagina"
                :key="item.codigo"
                :class="[item.fora_base ? 'fora-base' : item.qtd_atual > 0 ? 'retido' : 'nao-retido', { bloqueado: !podeEditar(item) }]"
              >
                <td class="col-dist">
                  <div class="nome" :title="item.nome">{{ item.nome }}</div>
                  <div class="sub">
                    <span class="cod">{{ item.codigo }}</span> · {{ item.cidade }}/{{ item.uf }}
                  </div>
                  <div class="tags">
                    <span :class="['tag', 'classe-' + item.classe]">{{ nomeClasse(item.classe) }}</span>
                    <span class="tag">Reg. {{ item.regiao }}</span>
                    <span class="tag" title="Situação do cliente">Sit. {{ item.situacao }}</span>
                    <span
                      v-if="mesCadastro(item) >= mesAnteriorCompetencia"
                      class="tag novo"
                      :title="'Cadastrado em ' + formataData(item.data_cadastro)"
                    >Novo {{ rotuloMesSo(mesCadastro(item)) }}</span>
                    <span
                      v-if="mesReativacao(item) >= mesAnteriorCompetencia"
                      class="tag reativado"
                      :title="'Reativado em ' + formataData(item.data_reativacao)"
                    >Reativado {{ rotuloMesSo(mesReativacao(item)) }}</span>
                    <span
                      v-if="item.fora_base"
                      class="tag fora"
                      title="Entrou depois do fechamento da base: não conta na meta deste mês"
                    >fora da meta</span>
                    <span v-if="item.consultor" class="consultor" :title="'Consultor: ' + item.consultor">
                      <i class="fas fa-user-tie"></i> {{ item.consultor }}
                    </span>
                  </div>
                </td>
                <td class="col-hist">
                  <div
                    v-for="linha in linhasHist"
                    :key="'l' + linha.ano"
                    :class="['hist', { 'com-ano': mostraAnoAnterior }]"
                  >
                    <span v-if="mostraAnoAnterior" class="ano-cab">{{ String(linha.ano).substr(2) }}</span>
                    <span
                      v-for="m in linha.meses"
                      :key="'c' + m"
                      :class="['cel', m > mesCompetencia ? 'futuro' : nivelMes(item, m), { atual: m === mesCompetencia }]"
                      :title="m > mesCompetencia ? '' : dicaMes(item, m)"
                    >
                      <template v-if="m <= mesCompetencia && qtd(item, m)">
                        <span class="q">{{ formataCurto(qtd(item, m)) }}</span>
                        <span v-if="qtd(item, m, 'valor_mes')" class="rs">{{ formataCurtoReal(qtd(item, m, 'valor_mes')) }}</span>
                      </template>
                    </span>
                  </div>
                </td>
                <td class="col-num">
                  {{ formataQtd(media(item, mesesAnterior)) || '–' }}
                  <div v-if="media(item, mesesAnterior, 'valor_mes')" class="valor">
                    {{ formataReal(media(item, mesesAnterior, 'valor_mes')) }}
                  </div>
                </td>
                <td class="col-num">
                  {{ formataQtd(media(item, mesesAtual)) || '–' }}
                  <div v-if="media(item, mesesAtual, 'valor_mes')" class="valor">
                    {{ formataReal(media(item, mesesAtual, 'valor_mes')) }}
                  </div>
                </td>
                <td class="col-mes">
                  <div class="qtd-mes">{{ formataQtd(item.qtd_atual) || '0' }} <small>itens</small></div>
                  <div v-if="item.valor_atual > 0" class="valor-mes">{{ formataReal(item.valor_atual) }}</div>
                  <span :class="['selo', item.qtd_atual > 0 ? 'sim' : 'nao']">
                    {{ item.qtd_atual > 0 ? 'Retido' : 'Não retido' }}
                  </span>
                </td>
                <td class="col-semana">
                  <template v-if="item.semana">
                    <div :class="['semana', { hoje: item.semana === semanaHoje }]" :title="dicaSemana(item)">
                      <b>{{ item.semana }}ª</b>
                      <span class="dias">{{ diasSemana(item.semana) }}</span>
                    </div>
                    <div class="barras" :title="dicaSemana(item)">
                      <span
                        v-for="(p, i) in item.semana_pct"
                        :key="'s' + i"
                        :class="['barra', { topo: i + 1 === item.semana }]"
                        :style="{ height: Math.max(2, Math.round((p / 100) * 24)) + 'px' }"
                      ></span>
                    </div>
                    <div v-if="item.semana === semanaHoje" class="cobrar">semana atual</div>
                  </template>
                  <span v-else class="sub">–</span>
                </td>
                <td class="col-contato">
                  <template v-if="item.data_contato">
                    {{ formataData(item.data_contato) }}
                    <div v-if="item.contato_em" class="hora">{{ String(item.contato_em).substr(11, 5) }}</div>
                    <div class="sub" :title="item.contato_usuario">{{ primeiroNome(item.contato_usuario) }}</div>
                  </template>
                  <span v-else class="sub">–</span>
                </td>
                <td class="col-motivo">
                  <input
                    v-model="item.motivo"
                    class="campo"
                    list="retencao-motivos"
                    maxlength="255"
                    placeholder="Informar motivo…"
                    :readonly="!podeEditar(item)"
                    :title="podeEditar(item) ? '' : 'Fora da sua região (somente leitura)'"
                    @change="salvar(item)"
                    @keyup.enter="$event.target.blur()"
                  />
                </td>
                <td class="col-obs">
                  <div class="d-flex align-items-center">
                    <input
                      v-model="item.observacao"
                      class="campo"
                      maxlength="2000"
                      placeholder="Observação…"
                      :readonly="!podeEditar(item)"
                      :title="podeEditar(item) ? item.observacao : 'Fora da sua região (somente leitura)'"
                      @change="salvar(item)"
                      @keyup.enter="$event.target.blur()"
                    />
                  </div>
                </td>
                <td class="col-cat">
                  <template v-if="item.catalogos !== null && item.catalogos !== undefined">
                    <div class="qtd-cat">{{ formataQtd(item.catalogos) || '0' }}</div>
                    <div v-if="item.catalogo_data" class="sub">{{ formataData(item.catalogo_data) }}</div>
                  </template>
                  <span v-else class="sub">–</span>
                </td>
                <td class="col-opcat">
                  <div class="d-flex align-items-center">
                    <input
                      v-model="item.op_cat"
                      class="campo"
                      maxlength="30"
                      placeholder="Op. Cat…"
                      :readonly="!podeEditar(item)"
                      :title="podeEditar(item) ? '' : 'Fora da sua região (somente leitura)'"
                      @change="salvar(item)"
                      @keyup.enter="$event.target.blur()"
                    />
                    <span class="status">
                      <b-spinner v-if="item._salvando" small></b-spinner>
                      <i v-else-if="item._salvo" class="fas fa-check-circle text-success" title="Salvo"></i>
                      <i v-else-if="item._erro" class="fas fa-exclamation-triangle text-danger" :title="item._erro"></i>
                      <i v-else-if="!podeEditar(item)" class="fas fa-lock text-muted" title="Fora da sua região"></i>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <datalist id="retencao-motivos">
          <option v-for="m in motivos" :key="m" :value="m"></option>
        </datalist>

        <b-row class="mt-2" v-if="filtrados.length > porPagina">
          <b-col>
            <b-pagination v-model="paginaAtual" :total-rows="filtrados.length" :per-page="porPagina" size="sm"></b-pagination>
          </b-col>
        </b-row>
      </template>
    </b-card>
  </div>
</template>

<script>
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
// preferencia do proprio navegador (pode nao existir: aba anonima, bloqueio)
const lerPreferencia = chave => {
  try {
    return window.localStorage.getItem(chave);
  } catch (e) {
    return null;
  }
};
const gravarPreferencia = (chave, valor) => {
  try {
    window.localStorage.setItem(chave, valor);
  } catch (e) {
    // sem armazenamento: so nao lembra a escolha
  }
};

// semana do mes: 1 = dias 1-7, 2 = 8-14, 3 = 15-21, 4 = 22 ate o fim
const semanaDoDia = dia => Math.min(4, Math.ceil(dia / 7));

const CLASSES = { A: 'Master', G: 'Gestora', L: 'L', D: 'D', B: 'B' };

export default {
  props: {
    // regiao do usuario (users.regiao_retencao): filtro inicial e base fixa das metas
    regiaoUsuario: { type: String, default: null },
    usuarioId: { type: Number, default: null },
    podeCadastrarMeta: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      competencias: [],
      competenciaSel: null,
      competencia: null,
      itens: [],
      metas: [],
      motivos: [],
      mostraAnoAnterior: lerPreferencia('retencao.anoAnterior') === '1',
      metricaGrafico: 'valor_mes',
      paginaAtual: 1,
      porPagina: 100,
      filtro: {
        regiao: this.regiaoUsuario || null,
        classe: null,
        retido: null,
        busca: '',
        motivo: null,
        semana: null,
        cadastro: null,
        reativacao: null,
      },
    };
  },
  computed: {
    mesCompetencia() {
      return this.competencia ? this.competencia.competencia.replace('-', '') : null;
    },
    anoAtual() {
      return this.mesCompetencia ? Number(this.mesCompetencia.substr(0, 4)) : null;
    },
    anoAnterior() {
      return this.anoAtual ? this.anoAtual - 1 : null;
    },
    mesesAnterior() {
      return this.mesesDoAno(this.anoAnterior, 12);
    },
    mesesAtual() {
      return this.mesesDoAno(this.anoAtual, Number(this.mesCompetencia.substr(4, 2)));
    },
    // linhas do mapa: 12 meses moveis, ou ano anterior + ano atual (jan-dez)
    linhasHist() {
      if (this.mostraAnoAnterior) {
        return [
          { ano: this.anoAnterior, meses: this.mesesDoAno(this.anoAnterior, 12) },
          { ano: this.anoAtual, meses: this.mesesDoAno(this.anoAtual, 12) },
        ];
      }
      return [{ ano: 0, meses: this.meses12 }];
    },
    // janela do mapa de calor: 12 meses terminando na competencia
    meses12() {
      const ano = this.anoAtual;
      const mes = Number(this.mesCompetencia.substr(4, 2));
      const meses = [];
      for (let i = 11; i >= 0; i--) {
        const d = new Date(ano, mes - 1 - i, 1);
        meses.push(d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0'));
      }
      return meses;
    },
    // a meta e sempre da regiao da analista; sem regiao no usuario, segue o filtro
    regiaoMeta() {
      return this.regiaoUsuario || this.filtro.regiao;
    },
    metasDaRegiao() {
      return this.regiaoMeta ? this.metas.filter(m => m.regiao === this.regiaoMeta) : [];
    },
    // faixas da propria analista; sem ela, as de quem cuida da regiao filtrada
    metaAnalista() {
      const propria = this.metas.find(m => m.user_id === this.usuarioId);
      if (this.regiaoUsuario && propria) {
        return propria;
      }
      return this.metasDaRegiao.length ? this.metasDaRegiao[0] : null;
    },
    // por faixa: quantos precisa reter, quantos faltam e quanto ja atingiu
    faixas() {
      if (!this.metaAnalista) {
        return [];
      }
      const ativos = this.resumo.ativos;
      const retidos = this.resumo.retidos;
      return this.metaAnalista.faixas.map(f => {
        const meta = Number(f.meta);
        const precisa = Math.ceil(ativos * meta / 100);
        return {
          faixa: f.faixa,
          meta,
          premio: f.premio !== null ? Number(f.premio) : null,
          precisa,
          faltam: precisa - retidos,
          atingido: precisa ? (retidos / precisa) * 100 : 0,
        };
      });
    },
    // base de ativos da meta (sem os cadastrados no mes da competencia)
    itensBase() {
      return this.itens.filter(i => !i.fora_base);
    },
    // cadastrados/reativados no mes da competencia (so contato), na regiao da meta
    foraBase() {
      const fora = this.itens.filter(i => i.fora_base && (!this.regiaoMeta || i.regiao === this.regiaoMeta));
      return {
        novos: fora.filter(i => this.mesCadastro(i) === this.mesCompetencia).length,
        reativados: fora.filter(i => this.mesCadastro(i) !== this.mesCompetencia).length,
      };
    },
    resumo() {
      const base = this.regiaoMeta ? this.itensBase.filter(i => i.regiao === this.regiaoMeta) : this.itensBase;
      const r = { ativos: base.length, ativosA: 0, ativosG: 0, retidos: 0, retidosA: 0, retidosG: 0, semMotivo: 0, pct: 0, motivos: [] };
      const motivos = {};
      base.forEach(i => {
        const retido = i.qtd_atual > 0;
        if (i.classe === 'A') r.ativosA++;
        if (i.classe === 'G') r.ativosG++;
        if (retido) {
          r.retidos++;
          if (i.classe === 'A') r.retidosA++;
          if (i.classe === 'G') r.retidosG++;
        } else if (!i.motivo) {
          r.semMotivo++;
        }
        if (i.motivo) {
          motivos[i.motivo] = (motivos[i.motivo] || 0) + 1;
        }
      });
      r.pct = r.ativos ? (r.retidos / r.ativos) * 100 : 0;
      r.motivos = Object.keys(motivos)
        .map(m => ({ motivo: m, qtd: motivos[m] }))
        .sort((a, b) => b.qtd - a.qtd);
      return r;
    },
    opcoesRegiao() {
      const regioes = [...new Set(this.itens.map(i => i.regiao).filter(Boolean))]
        .sort((a, b) => Number(a) - Number(b));
      return [{ value: null, text: 'Todas' }].concat(regioes.map(r => ({ value: r, text: 'Região ' + r })));
    },
    opcoesClasse() {
      const classes = [...new Set(this.itens.map(i => i.classe).filter(Boolean))].sort();
      return [{ value: null, text: 'Todas' }].concat(classes.map(c => ({ value: c, text: CLASSES[c] || c })));
    },
    opcoesRetido() {
      return [
        { value: null, text: 'Todos' },
        { value: true, text: 'Retidos' },
        { value: false, text: 'Não retidos' },
        { value: 'sem_motivo', text: 'Não retidos sem motivo' },
      ];
    },
    mesAnoAnterior() {
      return this.anoAnterior + this.mesCompetencia.substr(4, 2);
    },
    // base do card de vendas: os filtros da grade, exceto busca e motivo
    baseVendas() {
      const f = this.filtro;
      return this.itensBase.filter(i => (!f.regiao || i.regiao === f.regiao) && (!f.classe || i.classe === f.classe));
    },
    // soma por mes (ano anterior + ano atual) de itens e R$
    somaMes() {
      const soma = {};
      this.mesesAnterior.concat(this.mesesDoAno(this.anoAtual, 12)).forEach(m => {
        soma[m] = { qtd: 0, valor: 0 };
      });
      this.baseVendas.forEach(i => {
        Object.keys(soma).forEach(m => {
          soma[m].qtd += this.qtd(i, m);
          soma[m].valor += this.qtd(i, m, 'valor_mes');
        });
      });
      return soma;
    },
    totais() {
      const s = this.somaMes;
      const variacao = (a, b) => (b ? ((a - b) / b) * 100 : null);
      const mes = s[this.mesCompetencia];
      const ant = s[this.mesAnoAnterior];
      const acum = { qtd: 0, valor: 0, qtdAnt: 0, valorAnt: 0 };
      this.mesesAtual.forEach(m => {
        const mAnt = this.anoAnterior + m.substr(4, 2);
        acum.qtd += s[m].qtd;
        acum.valor += s[m].valor;
        acum.qtdAnt += s[mAnt].qtd;
        acum.valorAnt += s[mAnt].valor;
      });
      acum.varValor = variacao(acum.valor, acum.valorAnt);
      acum.varQtd = variacao(acum.qtd, acum.qtdAnt);
      return {
        mes: {
          qtd: mes.qtd,
          valor: mes.valor,
          qtdAnt: ant.qtd,
          valorAnt: ant.valor,
          varValor: variacao(mes.valor, ant.valor),
          varQtd: variacao(mes.qtd, ant.qtd),
        },
        acum,
      };
    },
    // barras agrupadas jan..mes da competencia: ano anterior x ano atual
    grafico() {
      const campo = this.metricaGrafico === 'valor_mes' ? 'valor' : 'qtd';
      const largura = 600;
      const altura = 136;
      const base = altura - 14;
      const meses = this.mesesAtual;
      const grupo = largura / Math.max(meses.length, 1);
      const w = Math.min(18, (grupo - 10) / 2);
      const pares = meses.map(m => [this.somaMes[this.anoAnterior + m.substr(4, 2)][campo], this.somaMes[m][campo]]);
      const max = Math.max(1, ...pares.map(p => Math.max(p[0], p[1])));
      const y = v => base - (v / max) * (base - 18);
      const rotulo = (m, v) =>
        this.rotuloMes(m) + ': ' + (campo === 'valor' ? this.formataReal(v) : this.formataQtd(v) + ' itens');
      return {
        largura,
        altura,
        base,
        barras: meses.map((m, k) => {
          const mAnt = this.anoAnterior + m.substr(4, 2);
          const qAnt = pares[k][0];
          const qAtu = pares[k][1];
          const dif = qAnt || qAtu ? qAtu - qAnt : null;
          return {
            mes: m,
            nome: this.rotuloMesSo(m),
            x: k * grupo + (grupo - (w * 2 + 2)) / 2,
            w,
            yAnt: y(pares[k][0]),
            yAtu: y(pares[k][1]),
            atual: m === this.mesCompetencia,
            rotuloAnt: rotulo(mAnt, pares[k][0]),
            rotuloAtu: rotulo(m, pares[k][1]),
            difQtd: dif,
            difCurta: campo === 'valor' ? this.formataDifCurta(dif, 'R$') : this.formataDifCurta(dif),
            rotuloDif:
              campo === 'valor'
                ? this.rotuloMesSo(m) + ': ' + this.formataDifReal(dif) + ' vs ' + this.anoAnterior +
                  ' (' + this.formataReal(qAnt) + ' → ' + this.formataReal(qAtu) + ')'
                : this.rotuloMesSo(m) + ': ' + this.formataDif(dif) + ' itens vs ' + this.anoAnterior +
                  ' (' + this.formataQtd(qAnt) + ' → ' + this.formataQtd(qAtu) + ')',
          };
        }),
      };
    },
    mesAnteriorCompetencia() {
      const d = new Date(this.anoAtual, Number(this.mesCompetencia.substr(4, 2)) - 2, 1);
      return d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0');
    },
    opcoesSemana() {
      return [{ value: null, text: 'Todas' }].concat(
        [1, 2, 3, 4].map(s => ({
          value: s,
          text: s + 'ª (' + this.diasSemana(s) + ')' + (s === this.semanaHoje ? ' · atual' : ''),
        }))
      );
    },
    // semana de hoje, so quando a competencia e o mes corrente
    semanaHoje() {
      const hoje = new Date();
      const mes = hoje.getFullYear() + String(hoje.getMonth() + 1).padStart(2, '0');
      return mes === this.mesCompetencia ? semanaDoDia(hoje.getDate()) : null;
    },
    ultimoDiaMes() {
      return new Date(this.anoAtual, Number(this.mesCompetencia.substr(4, 2)), 0).getDate();
    },
    filtrados() {
      const f = this.filtro;
      const busca = (f.busca || '').trim().toUpperCase();
      return this.itens.filter(i => {
        if (f.regiao && i.regiao !== f.regiao) return false;
        if (f.classe && i.classe !== f.classe) return false;
        if (f.motivo && i.motivo !== f.motivo) return false;
        if (f.semana && i.semana !== f.semana) return false;
        if (!this.noPeriodo(this.mesCadastro(i), f.cadastro)) return false;
        if (!this.noPeriodo(this.mesReativacao(i), f.reativacao)) return false;
        if (f.retido === true && !(i.qtd_atual > 0)) return false;
        if (f.retido === false && i.qtd_atual > 0) return false;
        if (f.retido === 'sem_motivo' && (i.qtd_atual > 0 || i.motivo)) return false;
        if (busca) {
          const alvo = [i.codigo, i.nome, i.cidade, i.consultor, i.motivo, i.observacao, i.op_cat].join(' ').toUpperCase();
          if (alvo.indexOf(busca) < 0) return false;
        }
        return true;
      });
    },
    pagina() {
      const ini = (this.paginaAtual - 1) * this.porPagina;
      return this.filtrados.slice(ini, ini + this.porPagina);
    },
  },
  watch: {
    mostraAnoAnterior(v) {
      gravarPreferencia('retencao.anoAnterior', v ? '1' : '0');
    },
    filtrados() {
      this.paginaAtual = 1;
    },
  },
  mounted() {
    this.carregar();
  },
  methods: {
    carregar() {
      const vm = this;
      vm.loading = true;
      axios
        .get('/adm/retencao/dados', { params: { competencia: vm.competenciaSel } })
        .then(response => {
          const d = response.data;
          vm.competencias = d.competencias;
          vm.competencia = d.competencia;
          vm.competenciaSel = d.competencia ? d.competencia.competencia : null;
          vm.motivos = d.motivos;
          vm.metas = d.metas || [];
          const meses12 = vm.meses12;
          vm.itens = d.itens.map(i => ({
            ...i,
            qtd_mes: i.qtd_mes || {},
            valor_mes: i.valor_mes || {},
            valor_atual: Number(i.valor_atual) || 0,
            _media12: vm.media({ qtd_mes: i.qtd_mes || {} }, meses12),
            _original: { motivo: i.motivo || '', observacao: i.observacao || '', op_cat: i.op_cat || '' },
            _salvando: false,
            _salvo: false,
            _erro: null,
          }));
        })
        .catch(error => {
          vm.$swal('Erro', vm.mensagemErro(error, 'Falha ao carregar a retenção'), 'error');
        })
        .finally(() => {
          vm.loading = false;
        });
    },
    // analista com regiao so edita a propria; sem regiao (admin/gestao) edita todas
    podeEditar(item) {
      return !this.regiaoUsuario || item.regiao === this.regiaoUsuario;
    },
    salvar(item) {
      if (!this.podeEditar(item)) {
        return;
      }
      const motivo = (item.motivo || '').trim();
      const observacao = (item.observacao || '').trim();
      const op_cat = (item.op_cat || '').trim();
      if (
        motivo.toUpperCase() === item._original.motivo &&
        observacao === item._original.observacao &&
        op_cat === item._original.op_cat
      ) {
        return;
      }
      item._salvando = true;
      item._salvo = false;
      item._erro = null;
      axios
        .put(`/adm/retencao/${this.competencia.competencia}/${encodeURIComponent(item.codigo)}`, { motivo, observacao, op_cat })
        .then(response => {
          const d = response.data;
          item.motivo = d.motivo;
          item.observacao = d.observacao;
          item.op_cat = d.op_cat;
          item.data_contato = d.data_contato;
          item.contato_em = d.contato_em;
          item.contato_usuario = d.contato_usuario;
          item._original = { motivo: d.motivo || '', observacao: d.observacao || '', op_cat: d.op_cat || '' };
          item._salvo = true;
          if (d.motivo && this.motivos.indexOf(d.motivo) < 0) {
            this.motivos.push(d.motivo);
          }
        })
        .catch(error => {
          item._erro = this.mensagemErro(error, 'Falha ao salvar');
        })
        .finally(() => {
          item._salvando = false;
        });
    },
    exportar() {
      const params = new URLSearchParams({ competencia: this.competencia.competencia });
      if (this.filtro.regiao) {
        params.append('regiao', this.filtro.regiao);
      }
      window.open('/adm/retencao/exportar?' + params.toString(), '_blank');
    },
    mesesDoAno(ano, ate) {
      const meses = [];
      for (let m = 1; m <= ate; m++) {
        meses.push(ano + String(m).padStart(2, '0'));
      }
      return meses;
    },
    qtd(item, mes, campo = 'qtd_mes') {
      return (item[campo] && item[campo][mes]) || 0;
    },
    media(item, meses, campo = 'qtd_mes') {
      const valores = meses.map(m => this.qtd(item, m, campo)).filter(v => v > 0);
      return valores.length ? valores.reduce((a, b) => a + b, 0) / valores.length : 0;
    },
    // cor do mes em relacao a media do proprio distribuidor nos 12 meses
    nivelMes(item, mes) {
      const q = this.qtd(item, mes);
      if (!q) return 'n0';
      if (q < item._media12 * 0.8) return 'n1';
      if (q <= item._media12 * 1.2) return 'n2';
      return 'n3';
    },
    // AAAAMM do cadastro ('' sem data)
    mesCadastro(item) {
      return item.data_cadastro ? String(item.data_cadastro).substr(0, 7).replace('-', '') : '';
    },
    mesReativacao(item) {
      return item.data_reativacao ? String(item.data_reativacao).substr(0, 7).replace('-', '') : '';
    },
    // opcoes dos filtros Cadastro/Reativacao: montadas a partir da competencia
    // aberta, entao os meses (e as contagens) acompanham a troca de competencia
    opcoesPeriodo(tipo) {
      const mesDe = tipo === 'cadastro' ? this.mesCadastro : this.mesReativacao;
      const conta = mes => this.itens.filter(i => mesDe(i) === mes).length;
      const rotulo = mes => (tipo === 'cadastro' ? 'Cadastrados' : 'Reativados') + ' em ' + this.rotuloMes(mes);
      return [
        { value: null, text: 'Todos' },
        { value: 'mes', text: rotulo(this.mesCompetencia) + ' (' + conta(this.mesCompetencia) + ')' },
        { value: 'anterior', text: rotulo(this.mesAnteriorCompetencia) + ' (' + conta(this.mesAnteriorCompetencia) + ')' },
        { value: 'seis', text: 'Últimos 6 meses' },
      ];
    },
    noPeriodo(mes, periodo) {
      if (!periodo) return true;
      if (periodo === 'mes') return mes === this.mesCompetencia;
      if (periodo === 'anterior') return mes === this.mesAnteriorCompetencia;
      return mes > this.meses12[5] && mes <= this.mesCompetencia;
    },
    filtrarMes(tipo) {
      this.filtro.cadastro = tipo === 'cadastro' ? 'mes' : null;
      this.filtro.reativacao = tipo === 'reativacao' ? 'mes' : null;
    },
    dicaMes(item, mes) {
      const q = this.qtd(item, mes);
      if (!q) return this.rotuloMes(mes) + ': sem pedido';
      const v = this.qtd(item, mes, 'valor_mes');
      return this.rotuloMes(mes) + ': ' + this.formataQtd(q) + ' itens' + (v ? ' · ' + this.formataReal(v) : '');
    },
    diasSemana(s) {
      const ini = (s - 1) * 7 + 1;
      const fim = s === 4 ? this.ultimoDiaMes : s * 7;
      return String(ini).padStart(2, '0') + '–' + fim;
    },
    dicaSemana(item) {
      const pct = item.semana_pct || [];
      return (
        'Compras (R$) por semana nos últimos 12 meses: ' +
        [1, 2, 3, 4].map(s => s + 'ª ' + this.formataPct(pct[s - 1] || 0)).join(' · ')
      );
    },
    rotuloMesSo(mes) {
      return MESES[Number(mes.substr(4, 2)) - 1].toLowerCase();
    },
    rotuloMesCurto(mes) {
      const m = MESES[Number(mes.substr(4, 2)) - 1].toLowerCase();
      return mes.substr(4, 2) === '01' ? m + '/' + mes.substr(2, 2) : m;
    },
    formataCurto(v) {
      if (!v) return '';
      if (v >= 10000) return Math.round(v / 1000) + 'k';
      if (v >= 1000) return (v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'k';
      return String(Math.round(v));
    },
    formataDif(v) {
      return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(Math.round(v)).toLocaleString('pt-BR');
    },
    formataDifCurta(v, prefixo = '') {
      return (v > 0 ? '+' : v < 0 ? '−' : '') + prefixo + (this.formataCurto(Math.abs(v)) || '0');
    },
    formataDifReal(v) {
      return (v > 0 ? '+' : v < 0 ? '−' : '') + this.formataReal(Math.abs(v));
    },
    formataVar(v) {
      if (v === null) return 'sem base';
      return (v > 0 ? '▲ ' : v < 0 ? '▼ ' : '') + Math.abs(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';
    },
    classeVar(v) {
      return v === null ? '' : v >= 0 ? 'sobe' : 'desce';
    },
    formataCurtoReal(v) {
      return v ? 'R$' + this.formataCurto(v) : '';
    },
    nomeClasse(c) {
      return CLASSES[c] || 'Classe ' + c;
    },
    primeiroNome(nome) {
      return nome ? String(nome).split(' ')[0] : '';
    },
    rotuloMes(mes) {
      return MESES[Number(mes.substr(4, 2)) - 1] + '/' + mes.substr(2, 2);
    },
    nomeCompetencia(c) {
      const [ano, mes] = c.split('-');
      return MESES[Number(mes) - 1] + ' / ' + ano;
    },
    formataQtd(v) {
      return v ? Math.round(v).toLocaleString('pt-BR') : '';
    },
    formataReal(v) {
      return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
    },
    formataPct(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + '%';
    },
    formataData(d) {
      if (!d) return '';
      const [ano, mes, dia] = String(d).substr(0, 10).split('-');
      return `${dia}/${mes}/${ano}`;
    },
    formataDataHora(d) {
      return d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
    },
    mensagemErro(error, padrao) {
      const d = error.response && error.response.data;
      return (d && (d.erro || d.message)) || padrao;
    },
  },
};
</script>

<style scoped>
.card-resumo {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  height: 100%;
  background: #fff;
}
.card-resumo .titulo {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
}
.card-resumo .valor {
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
}
.card-resumo .det {
  font-size: 11px;
  color: #6c757d;
}
.card-resumo.verde .valor {
  color: #1e7e34;
}
.card-resumo.vermelho .valor {
  color: #c82333;
}
.resumo > div {
  margin-bottom: 8px;
}
.faixas-wrap {
  overflow-x: auto;
}
.faixas {
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 4px;
}
.faixas th,
.faixas td {
  padding: 3px 12px;
  text-align: center;
  white-space: nowrap;
}
.faixas td:first-child {
  text-align: left;
  color: #6c757d;
  padding-left: 0;
}
.faixas th {
  font-weight: 600;
}
.faixas th.batida {
  color: #1e7e34;
}
.faixas td.ok {
  background: #c6efce;
  border: 1px solid #1e7e34;
  font-weight: 600;
}
.faixas td.falta {
  background: #fde2dc;
  border: 1px solid #c82333;
  font-weight: 600;
}
.faixas td.atingido {
  background: #ffff00;
  border: 1px solid #e0a800;
  font-weight: 600;
}
.troca-metrica a {
  margin-left: 8px;
  color: #6c757d;
  text-transform: none;
}
.troca-metrica a.ativo {
  color: #0b5ed7;
  font-weight: 600;
  text-decoration: underline;
}
.totais {
  display: flex;
  gap: 24px;
  margin-top: 4px;
}
.totais .rot {
  font-size: 11px;
  color: #6c757d;
}
.totais .num {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}
.totais .num small {
  font-size: 11px;
  font-weight: 400;
  color: #6c757d;
}
.totais .num-rs {
  font-size: 15px;
  font-weight: 600;
  color: #1e6b33;
}
.var {
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
}
.var.sobe {
  color: #1e7e34;
}
.var.desce {
  color: #c82333;
}
.grafico {
  width: 100%;
  height: auto;
  max-height: 160px;
  margin-top: 6px;
  display: block;
}
.grafico .eixo {
  stroke: #dee2e6;
  stroke-width: 1;
}
.grafico .dif {
  font-size: 9px;
  font-weight: 600;
  text-anchor: middle;
}
.grafico .dif.sobe {
  fill: #1e7e34;
}
.grafico .dif.desce {
  fill: #c82333;
}
.grafico .rot-mes {
  font-size: 10px;
  fill: #6c757d;
  text-anchor: middle;
}
.b-ant {
  fill: #ced4da;
  background: #ced4da;
}
.b-atu {
  fill: #6ea8fe;
  background: #6ea8fe;
}
.b-atu.atual {
  fill: #0b5ed7;
}
.legenda-graf {
  font-size: 11px;
  color: #495057;
}
.legenda-graf span {
  margin-right: 10px;
}
.legenda-graf i {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  vertical-align: -1px;
  margin-right: 3px;
}
.motivo-badge {
  cursor: pointer;
  font-weight: normal;
  border: 1px solid #dee2e6;
}
.legenda {
  font-size: 12px;
  color: #6c757d;
}
.legenda span {
  margin-left: 12px;
  white-space: nowrap;
}
.legenda .cel {
  display: inline-block;
  width: 14px;
  height: 14px;
  vertical-align: -2px;
  margin-right: 3px;
}

/* ------- grade ------- */
.grade {
  overflow: auto;
  max-height: 72vh;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  background: #fff;
}
.tabela {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.tabela thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f4f6f8;
  color: #495057;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 10px;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
  vertical-align: bottom;
}
.tabela tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #eef0f2;
  vertical-align: middle;
}
.tabela tbody tr:hover td {
  background: #f7fbff;
}
.tabela tbody tr.retido td:first-child {
  box-shadow: inset 4px 0 0 #28a745;
}
.tabela tbody tr.nao-retido td:first-child {
  box-shadow: inset 4px 0 0 #dc3545;
}
.tabela tbody tr.fora-base td:first-child {
  box-shadow: inset 4px 0 0 #e0a800;
}
.tabela .vazio {
  text-align: center;
  color: #6c757d;
  padding: 24px;
}

/* distribuidor: fixo a esquerda quando a tela e estreita */
.col-dist {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  min-width: 250px;
  max-width: 280px;
}
.tabela thead .col-dist {
  z-index: 3;
  background: #f4f6f8;
}
.col-dist .nome {
  font-weight: 600;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sub {
  font-size: 11px;
  color: #6c757d;
}
.cod {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  font-weight: 600;
  color: #495057;
}
.tags {
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.tag {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  background: #eef0f2;
  color: #495057;
  white-space: nowrap;
}
.tag.classe-A {
  background: #e7f1ff;
  color: #0b5ed7;
}
.tag.novo {
  background: #fff3cd;
  color: #8a6d00;
  font-weight: 600;
}
.tag.reativado {
  background: #d1f2eb;
  color: #0f6e56;
  font-weight: 600;
}
.tag.fora {
  background: #ffe08a;
  color: #6b5200;
}
.tag.classe-G {
  background: #f3e8ff;
  color: #6f42c1;
}
.consultor {
  font-size: 10px;
  color: #6c757d;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* mapa de calor dos 12 meses */
.col-hist {
  min-width: 590px;
}
.hist {
  display: grid;
  grid-template-columns: repeat(12, 44px);
  gap: 3px;
}
.hist.com-ano {
  grid-template-columns: 22px repeat(12, 44px);
}
.hist + .hist {
  margin-top: 3px;
}
.ano-cab {
  font-size: 10px;
  font-weight: 600;
  color: #6c757d;
  display: flex;
  align-items: center;
}
.cel.futuro {
  background: transparent;
  border: 1px dashed #e3e6ea;
}
.mes-cab {
  text-align: center;
  font-size: 10px;
}
.mes-cab.atual {
  color: #0b5ed7;
}
.cel {
  height: 34px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.15;
  cursor: default;
}
.cel .rs {
  font-size: 9px;
  font-weight: 500;
  opacity: 0.8;
  white-space: nowrap;
}
.cel.n0 {
  background: #fde8e8;
}
.cel.n1 {
  background: #fff3cd;
  color: #8a6d00;
}
.cel.n2 {
  background: #c6efce;
  color: #1e6b33;
}
.cel.n3 {
  background: #2e9e5b;
  color: #fff;
}
.cel.atual {
  outline: 2px solid #0b5ed7;
  outline-offset: 1px;
}

.col-num {
  text-align: right;
  white-space: nowrap;
  min-width: 70px;
  color: #495057;
}
.col-mes {
  text-align: center;
  min-width: 95px;
}
.qtd-mes {
  font-size: 15px;
  font-weight: 700;
}
.selo {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.selo.sim {
  background: #d4edda;
  color: #1e6b33;
}
.selo.nao {
  background: #f8d7da;
  color: #a71d2a;
}
.valor {
  font-size: 11px;
  color: #6c757d;
}
.qtd-mes small {
  font-size: 10px;
  font-weight: 400;
  color: #6c757d;
}
.valor-mes {
  font-size: 11px;
  font-weight: 600;
  color: #1e6b33;
  margin-bottom: 2px;
}
.col-semana {
  text-align: center;
  min-width: 90px;
}
.semana {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef0f2;
  white-space: nowrap;
}
.semana b {
  font-size: 13px;
}
.semana .dias {
  font-size: 10px;
  color: #6c757d;
}
.semana.hoje {
  background: #fff3cd;
  box-shadow: inset 0 0 0 1px #e0a800;
}
.barras {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 3px;
  height: 24px;
  margin-top: 4px;
}
.barra {
  width: 10px;
  border-radius: 2px 2px 0 0;
  background: #ced4da;
}
.barra.topo {
  background: #0b5ed7;
}
.cobrar {
  font-size: 10px;
  font-weight: 600;
  color: #8a6d00;
  text-transform: uppercase;
  margin-top: 2px;
}
.col-contato .hora {
  font-size: 11px;
  color: #495057;
}
.col-contato {
  white-space: nowrap;
  min-width: 85px;
}
.col-motivo {
  min-width: 190px;
}
.col-obs {
  min-width: 240px;
  width: 100%;
}
.col-cat {
  text-align: center;
  white-space: nowrap;
  min-width: 80px;
}
.qtd-cat {
  font-weight: 600;
}
.col-opcat {
  min-width: 130px;
}

/* campos editaveis: discretos ate o foco */
.campo {
  width: 100%;
  font-size: 12px;
  padding: 5px 8px;
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  background: #fafbfc;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.campo:hover {
  border-color: #c5ccd3;
}
.campo:focus {
  outline: none;
  background: #fff;
  border-color: #86b7fe;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
}
.campo[readonly] {
  background: #f1f3f5;
  color: #6c757d;
  border-color: transparent;
  cursor: not-allowed;
}
.status {
  width: 20px;
  text-align: center;
  margin-left: 4px;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/adm_retencao/Index.vue